import { ePHTRAbi } from 'abis/ePHTR'
import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { atom } from 'jotai'
import { AnatomyWithExtra, defaultIndexAnatomy } from 'services/query-v2/anatomy'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_OUTPUT_AMOUNT } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { viemPublicClients } from 'services/wallet/chains'
import { convertAmountToWei } from 'utils'
import { formatWeiToAmount } from '../../../../utils/numbers-helper'
import { OutputAmountResponse, OutputRequestAtoms, PriceParams, defaultPriceParams } from './model'

const PHTRtoEPHTROutputAmountQuery = async <T>({
  queryKey: [, params],
}: {
  queryKey: [string, PriceParams, AnatomyWithExtra]
}) => {
  if (params.sellAmount.eq(0)) {
    const defaultAmountResponse: OutputAmountResponse = {
      chainId: params.chainId,
      currency: params.sellCurrency,
      buyAmount: Big(0),
    }
    return defaultAmountResponse as T
  } else {
    const client = viemPublicClients[NetworkType.Mainnet]

    const withdrawableAmount = await client.readContract({
      address: params.buyCurrency.address, //check this is ePHTR
      abi: ePHTRAbi,
      functionName: 'withdrawableAmount',
      args: [BigInt(convertAmountToWei(Big(1), params.sellCurrency.decimals).toString())],
    })

    const withdrawableAmountToken = formatWeiToAmount(
      withdrawableAmount.toString(),
      params.sellCurrency.decimals,
    )
    const outputPrice = withdrawableAmountToken.eq(0) ? Big(0) : Big(1).div(withdrawableAmountToken)
    const outputAmount = outputPrice.mul(params.sellAmount)

    return parseOutput(params, outputAmount) as T
  }
}

const parseOutput = (params: PriceParams, buyAmount: Big): OutputAmountResponse => {
  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    buyAmount,
  }
}

export const PHTRtoEPHTROutputAmountAtomsFactory = () => {
  const paramsAtom = atom<PriceParams>(defaultPriceParams)
  const indexAnatomyAtom = atom<AnatomyWithExtra>(defaultIndexAnatomy)

  const mainProperties = createAtomsWithQuery<
    OutputAmountResponse,
    PriceParams,
    AnatomyWithExtra,
    null,
    [string, PriceParams, AnatomyWithExtra]
  >(
    { first: paramsAtom, second: indexAnatomyAtom },
    QUERY_KEY_OUTPUT_AMOUNT,
    PHTRtoEPHTROutputAmountQuery<OutputAmountResponse>,
    DEFAULT_REFETCH_INTERVAL,
  )

  return {
    params: paramsAtom,
    indexAnatomy: indexAnatomyAtom,
    ...mainProperties,
  } as OutputRequestAtoms
}
